<script setup lang="ts">
  import { toRef } from 'vue';
  import { IconImage } from '@/shared/ui';
  import { useContentLoadingState } from '@/shared/lib';
  import WidgetSkeleton, { type WidgetSkeletonVariant } from './WidgetSkeleton.vue';

  interface Props {
    title: string;
    periodLabel?: string;
    icon?: string | object;
    iconAlt?: string;
    loading?: boolean;
    hasData?: boolean;
    skeleton?: WidgetSkeletonVariant;
  }

  const props = withDefaults(defineProps<Props>(), {
    periodLabel: undefined,
    icon: undefined,
    iconAlt: '',
    loading: false,
    hasData: true,
    skeleton: 'bar-list',
  });

  const { showSkeleton, isRefreshing } = useContentLoadingState(
    toRef(props, 'loading'),
    toRef(props, 'hasData')
  );
</script>

<template>
  <section
    data-testid="widget-card"
    :aria-busy="props.loading ? 'true' : undefined"
    class="flex flex-col gap-5 overflow-clip rounded-2xl border border-border-color bg-white-01 p-6 shadow-[0_1px_2px_0_rgba(16,24,40,0.05)]"
  >
    <header class="flex shrink-0 flex-col gap-2">
      <div class="flex items-center gap-2">
        <span
          v-if="props.icon"
          class="flex size-7 items-center justify-center rounded-lg bg-gray-02 text-gray-07"
        >
          <IconImage
            :src="props.icon"
            :alt="props.iconAlt"
            class="size-6"
          />
        </span>
        <h2 class="text-sm font-normal text-gray-07">
          {{ props.title }}
        </h2>
      </div>
      <p
        v-if="props.periodLabel"
        class="text-left text-sm text-gray-03"
      >
        {{ props.periodLabel }}
      </p>
    </header>
    <div class="min-h-0 flex-1">
      <Transition
        name="widget-body"
        mode="out-in"
      >
        <WidgetSkeleton
          v-if="showSkeleton"
          :variant="props.skeleton"
        />
        <div
          v-else
          data-testid="widget-body"
          class="widget-body h-full"
          :class="{ 'widget-body--refreshing': isRefreshing }"
        >
          <slot />
        </div>
      </Transition>
    </div>
  </section>
</template>

<style scoped>
  .widget-body--refreshing {
    opacity: 0.55;
    pointer-events: none;
  }

  @media (prefers-reduced-motion: no-preference) {
    .widget-body-enter-active,
    .widget-body-leave-active {
      transition: opacity 0.2s ease-in-out;
    }

    .widget-body-enter-from,
    .widget-body-leave-to {
      opacity: 0;
    }

    .widget-body {
      transition: opacity 0.2s ease-in-out;
    }
  }
</style>
